import type { FetchError } from 'ofetch';
import type { _GettersTree } from 'pinia';

import { defineStore } from 'pinia';

import type { ProductsFilterMeta, ProductsFilterOptions } from '~/components/ProductList/Filters/types';
import type { Category } from '~/stores/category/types';
import type { Product } from '~/stores/product/types';

import { ProductSort, SortDirection } from '~/stores/category/types';
import { ApiMethod, handleApiError, type Pagination } from '~/utils/apiClient';

const STORE_ID = 'category';

interface Actions {
  fetchCategories: (locale: Locale) => Promise<Category[] | null>;
  fetchCategory: (slug: string, locale: Locale) => Promise<Category | null>;
  fetchCategoryProducts: (slug: string, locale: Locale) => Promise<Product[] | null>;
  setCurrentFilters: (filters: ProductsFilterOptions | null) => void;
  setCurrentProductSortBy: (value: ProductSort) => void;
  toggleCurrentProductSortDirection: () => void;
}

interface Getters extends _GettersTree<State> {
  getCategories: (state: State) => Category[] | null;
  getCategoryBySlug: (state: State) => (slug: string, locale?: Locale) => Category | null;
}

interface State {
  categories: Category[] | null;
  category: Category | null;
  currentFilters: ProductsFilterOptions | null;
  currentPage: number;
  currentProductSortBy: ProductSort;
  currentProductSortDirection: SortDirection;
  filters: FilterGroup[] | null;
  pagination: Pagination | null;
  products: Product[] | null;
}

export const useCategoryStore = defineStore<typeof STORE_ID, State, Getters, Actions>(STORE_ID, {
  actions: {
    async fetchCategories(locale: Locale) {
      try {
        const response = await this.$api.fetch<Category[]>(ApiMethod.GET, '/categories', { limit: 500, locale });

        if (response.data) {
          this.categories = response.data;
          return response.data;
        }
      } catch {
        await handleApiError('header.fetchCategoriesError', 'warning', 'general.errorTitle', true);

        return null;
      }

      return null;
    },
    async fetchCategory(slug: string, locale: Locale) {
      try {
        const response = await this.$api.fetch<Category>(ApiMethod.GET, `/categories/${slug}`, { locale });

        if (response.data) {
          this.category = response.data;
          return response.data;
        }
      } catch (error) {
        const fetchError = error as FetchError;
        await handleApiError(fetchError);

        return null;
      }

      return null;
    },
    async fetchCategoryProducts(slug: string, locale: Locale) {
      try {
        const response = await this.$api.fetch<Product[], ProductsFilterMeta>(
          ApiMethod.GET,
          `/categories/${slug}/products`,
          {
            filters: this.currentFilters ?? undefined,
            limit: DEFAULT_PAGE_LIMIT,
            locale,
            page: this.currentPage,
            sort_by: this.currentProductSortBy,
            sort_dir: this.currentProductSortDirection,
          },
        );

        if (response.data) {
          this.products = response.data;
          this.pagination = response.meta?.pagination ?? null;
          this.filters = response.meta?.filters ?? null;

          return response.data;
        }
      } catch (error) {
        const fetchError = error as FetchError;
        await handleApiError(fetchError);

        return null;
      }

      return null;
    },
    setCurrentFilters(filters: ProductsFilterOptions | null) {
      this.currentPage = 1;
      this.currentFilters = filters;
    },
    setCurrentProductSortBy(value: ProductSort) {
      this.currentPage = 1;
      this.currentProductSortBy = value;
    },
    toggleCurrentProductSortDirection() {
      this.currentPage = 1;
      this.currentProductSortDirection =
        this.currentProductSortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC;
    },
  },
  getters: {
    getCategories: (state) => state.categories?.sort((a, b) => Number(a.rank) - Number(b.rank)) ?? null,
    getCategoryBySlug:
      (state) =>
      (slug: string, locale?: Locale): Category | null => {
        if (!state.categories) return null;

        const findCategoryBySlug = (categories: Category[], slug: string, locale?: Locale): Category | null => {
          if (locale) {
            const category = categories.find((c) => c.slug[locale] === slug);
            if (category) return category;

            for (const parentCategory of categories) {
              if (parentCategory.categories && parentCategory.categories.length > 0) {
                const foundInChildren = findCategoryBySlug(parentCategory.categories, slug, locale);
                if (foundInChildren) return foundInChildren;
              }
            }
          } else {
            for (const category of categories) {
              const localeKeys = Object.keys(category.slug) as Locale[];
              for (const localeKey of localeKeys) {
                if (category.slug[localeKey] === slug) {
                  return category;
                }
              }
            }

            for (const parentCategory of categories) {
              if (parentCategory.categories && parentCategory.categories.length > 0) {
                const foundInChildren = findCategoryBySlug(parentCategory.categories, slug);
                if (foundInChildren) return foundInChildren;
              }
            }
          }

          return null;
        };

        return findCategoryBySlug(state.categories, slug, locale);
      },
  },
  state: () => ({
    categories: null,
    category: null,
    currentFilters: null,
    currentPage: 1,
    currentProductSortBy: ProductSort.NEWEST,
    currentProductSortDirection: SortDirection.ASC,
    filters: null,
    pagination: null,
    products: null,
  }),
});
